import { resolveApiUrl } from '@/providers/api-url';
import type {
  McpAuditItem,
  McpAuditPage,
  McpCoverage,
  McpCreatedToken,
  McpCreateTokenPayload,
  McpInfo,
  McpOAuthDecisionResult,
  McpOAuthRequest,
  McpPageMeta,
  McpTokenItem,
} from './types';

const API_URL = resolveApiUrl();

interface Envelope<T> {
  data: T;
  meta: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Достаёт человекочитаемую ошибку из конверта {errors} или OAuth-ответа {error_description}. */
function extractError(json: unknown, status: number): string {
  if (isRecord(json)) {
    const errors = json.errors;
    if (Array.isArray(errors) && typeof errors[0] === 'string') {
      return errors[0];
    }
    if (typeof json.error_description === 'string') {
      return json.error_description;
    }
    if (typeof json.message === 'string') {
      return json.message;
    }
  }
  if (status === 401) {
    return 'Сессия истекла — войдите в админку заново';
  }
  if (status === 403) {
    return 'Недостаточно прав';
  }
  return `Ошибка запроса (HTTP ${status})`;
}

/**
 * Запрос к MCP-эндпоинтам backend. Authorization и X-Tenant-Id проставляет
 * глобальный fetch-интерцептор (lib/fetch-with-auth.ts), он же делает refresh при 401.
 */
async function request<T>(path: string, init?: RequestInit): Promise<Envelope<T>> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
  const json: unknown = await response.json().catch(() => null);
  if (!response.ok || !isRecord(json) || json.success !== true) {
    throw new Error(extractError(json, response.status));
  }
  // Форма data гарантирована контрактом backend (см. types.ts).
  return { data: json.data as T, meta: json.meta };
}

function toPageMeta(meta: unknown, fallbackLimit: number): McpPageMeta {
  const record = isRecord(meta) ? meta : {};
  const num = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return {
    page: num(record.page, 1),
    limit: num(record.limit, fallbackLimit),
    total: num(record.total, 0),
    totalPages: num(record.totalPages, 1),
  };
}

/** Сведения о подключении. */
export async function fetchMcpInfo(): Promise<McpInfo> {
  return (await request<McpInfo>('/mcp/info')).data;
}

/** Список токенов: свои или все (all требует mcp.manage). */
export async function fetchMcpTokens(all: boolean, includeRevoked: boolean): Promise<McpTokenItem[]> {
  const params = new URLSearchParams({ all: String(all), includeRevoked: String(includeRevoked) });
  return (await request<McpTokenItem[]>(`/mcp/tokens?${params.toString()}`)).data;
}

/** Создать персональный токен. */
export async function createMcpToken(payload: McpCreateTokenPayload): Promise<McpCreatedToken> {
  return (await request<McpCreatedToken>('/mcp/tokens', { method: 'POST', body: JSON.stringify(payload) })).data;
}

/** Отозвать токен. */
export async function revokeMcpToken(id: number): Promise<void> {
  await request<{ revoked: boolean }>(`/mcp/tokens/${id}`, { method: 'DELETE' });
}

/** Отозвать все свои токены. */
export async function revokeAllMcpTokens(): Promise<number> {
  return (await request<{ revoked: number }>('/mcp/tokens/revoke-all', { method: 'POST' })).data.revoked;
}

/** Страница журнала вызовов. */
export async function fetchMcpAudit(all: boolean, page: number, limit: number): Promise<McpAuditPage> {
  const params = new URLSearchParams({ all: String(all), page: String(page), limit: String(limit) });
  const result = await request<McpAuditItem[]>(`/mcp/audit?${params.toString()}`);
  return { items: result.data, meta: toPageMeta(result.meta, limit) };
}

/** Отчёт покрытия API инструментами. */
export async function fetchMcpCoverage(): Promise<McpCoverage> {
  return (await request<McpCoverage>('/mcp/coverage')).data;
}

/** Публичные сведения о запросе OAuth-согласия. */
export async function fetchMcpOAuthRequest(id: string): Promise<McpOAuthRequest> {
  return (await request<McpOAuthRequest>(`/mcp/oauth/requests/${encodeURIComponent(id)}`)).data;
}

/**
 * Решение администратора по OAuth-запросу. Передаём текущий access-токен админки явно:
 * backend читает его из Authorization и сверяет claim tenants с площадкой запроса.
 * `tenant` — выбранная площадка (null — площадка домена, с которого пришёл запрос).
 */
export async function decideMcpOAuthRequest(
  id: string,
  approve: boolean,
  scopes: string[],
  tenant: string | null,
): Promise<McpOAuthDecisionResult> {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    throw new Error('Сессия истекла — войдите в админку заново');
  }
  const result = await request<McpOAuthDecisionResult>(
    `/mcp/oauth/requests/${encodeURIComponent(id)}/decision`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ approve, scopes, tenant }),
    },
  );
  return result.data;
}
